import clsx from 'clsx';
import {
  type FocusEvent as ReactFocusEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type PropsWithChildren,
  type RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';

import {
  ChevronRightDoubleIcon,
  DragAllDirectionIcon,
} from '@dynatrace/strato-icons';

import {
  chartToolbar,
  chartToolbarArea,
  defaultOverlayPadding,
  grip,
  outerContainer,
} from './ChartToolbarRenderer.sty.js';
import { ChartToolbarVisibilityOverlay } from './ChartToolbarVisibilityOverlay.js';
import { CollapseButton } from './CollapseButton.js';
import { ChartDataMenu } from './components/ChartDataMenu.js';
import { Control } from './components/Control.js';
import { HorizontalDivider, VerticalDivider } from './components/divider.js';
import { IntentsCollapsedAndDownloadSection } from './components/IntentsCollapsedAndDownloadSection.js';
import { ToolbarMenu } from './components/ToolbarMenu.js';
import { TOOLBAR_DRAG_BUTTON_ARIA_LABEL } from './constants.js';
import {
  ChartToolbarContext,
  type ChartToolbarContextProps,
} from './context/ChartToolbar.context.js';
import { useToolbar } from './hooks/useToolbar.js';
import { TOOLBAR_MESSAGES } from './messages.js';
import type { ChartToolbarRendererProps } from './types.js';
import { doesFitInBounds } from './utils/does-fit-in-bounds.js';
import { getToolbarPlacement } from './utils/get-toolbar-placement.js';
import { Button } from '../../../../buttons/button/Button.js';
import { useBehavioralTrackingProps as _useBehavioralTrackingProps } from '../../../../core/hooks/useBehavioralTrackingProps.js';
import { useMergeRefs } from '../../../../core/hooks/useMergeRefs.js';
import { Flex } from '../../../../layouts/flex/Flex.js';
import { Tooltip } from '../../../../overlays/tooltip/Tooltip.js';
import { A11yToolbarTabIndexContext } from '../../context/A11yToolbarTabIndexContext.js';
import { useA11yToolbarControls } from '../../hooks/useA11yToolbarControls.js';
import { useDragElement } from '../../hooks/useDragElement.js';
import { useObserveContainerSize } from '../../hooks/useObserveContainerSize.js';
import { getDraggableBounds } from '../../utils/get-draggable-bounds.js';
import { INTERACTION_MODE } from '../interactions-overlay/types.js';

/**
 * @internal
 */
export const InternalChartToolbarRenderer = (
  props: PropsWithChildren<ChartToolbarRendererProps>,
) => {
  const [
    behaviorTrackingProps,
    {
      children,
      visible = false,
      placement,
      collapsed = false,
      draggable = true,
      removeDragHandle = false,
      initialA11yState,
      zIndex,
      onToggleToolbar,
      onToggleMenu,
      onMouseEnter,
      onMouseLeave,
      menuIcon,
      showMenuDivider = false,
      menuTriggerLabel,
      menuDisplayMode = 'collapsed',
      onClose,
      isDownloadEnabled = false,
      intents,
      handleDownloadDataToolbar,
      toolbarDisplayMode = 'overlay',
      visibleWhenMouseInBounds = false,
      overlayClassName = defaultOverlayPadding,
      intentsAsyncTemplate,
      handlerRef,
    },
  ] = _useBehavioralTrackingProps(props);

  const {
    isExpanded,
    lastItemSelected,
    updateLastItemSelected,
    toggleExpanded,
    isCollapsable,
    fitsBounds,
    updateFitsBounds,
    shouldShowMenu,
  } = useToolbar(collapsed, menuDisplayMode);
  const isInlineMode = toolbarDisplayMode === 'inline';
  const [isMenuOpen, setIsMenuOpen] = useState<boolean | void>(undefined);
  const [isToolbarFocused, setIsToolbarFocused] = useState(false);
  const [toolbarElement, setToolbarElement] = useState<HTMLDivElement>();

  const [boundSize, boundRef] = useObserveContainerSize();
  const [toolbarSize, toolbarRefCallback] = useObserveContainerSize();
  const childrenRef = useRef<HTMLDivElement | null>(null);
  const lastMeasuredWidthRef = useRef<number>(0);
  const intl = useIntl();
  const mouseInBounds = useRef(false);
  const hasMountedRef = useRef(false);
  const menuClosedByOutsideInteractionRef = useRef(false);

  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    hasMountedRef.current = true;
  }, []);

  const toolbarComponentRef = useCallback((toolbarElement: HTMLDivElement) => {
    setToolbarElement(toolbarElement);
  }, []);

  const toolbarRef = useMergeRefs([toolbarRefCallback, toolbarComponentRef]);

  const { a11yControlsValue, setKeyEvent, setActiveControl } =
    useA11yToolbarControls(initialA11yState);

  const maxBounds = useMemo(
    () => getDraggableBounds(toolbarSize, boundSize),
    [toolbarSize, boundSize],
  );

  useEffect(() => {
    if (!isCollapsable) {
      toggleExpanded(!collapsed);
      return;
    }

    if (isExpanded && childrenRef.current) {
      const w = childrenRef.current.scrollWidth;
      if (w > 0) {
        lastMeasuredWidthRef.current = w;
      }
    }

    updateFitsBounds(doesFitInBounds(lastMeasuredWidthRef.current, boundSize));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCollapsable, isExpanded, boundSize]);

  useEffect(() => {
    toggleExpanded(fitsBounds && !collapsed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collapsed, fitsBounds]);

  const initialPosition = getToolbarPlacement(placement, maxBounds);

  const { isDragging, triggerDragRef, didDrag, setDidDrag } = useDragElement(
    toolbarElement,
    maxBounds,
    initialPosition,
  );

  const expandHandler = useCallback(
    (isExpanded: boolean) => {
      toggleExpanded(isExpanded);
      onToggleToolbar?.(isExpanded);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onToggleToolbar],
  );

  const handleToggleMenu = (open: boolean) => {
    if (!open) {
      if (!mouseInBounds.current) {
        onMouseLeave?.();
      } else {
        onMouseEnter?.();
      }

      requestAnimationFrame(() => setIsMenuOpen(open));
    } else {
      setIsMenuOpen(open);
    }
    onToggleMenu?.(open);
  };

  const collapseButton = useMemo(
    () =>
      Array.isArray(children) && (
        <CollapseButton
          onClick={(isExpanded: boolean) => {
            expandHandler(isExpanded);
            setActiveControl('explore');
          }}
        />
      ),
    [children, expandHandler, setActiveControl],
  );

  const expandButton = useMemo(
    () => (
      <Control
        prefixIcon={<ChevronRightDoubleIcon />}
        tooltipText={intl.formatMessage(TOOLBAR_MESSAGES.chartToolbarExpand)}
        onClick={() => expandHandler(true)}
        tabIndex={-1}
        testId="toolbar-expand-button"
        data-dt-name="ToolbarExpand"
      />
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [expandHandler],
  );

  const onMouseEnterHandler = () => {
    mouseInBounds.current = true;
    onMouseEnter?.();
  };

  const onMouseLeaveHandler = (event: ReactMouseEvent) => {
    event.stopPropagation();
    mouseInBounds.current = false;
    onMouseLeave?.();
  };

  const handleOnMenuEnter = () => {
    mouseInBounds.current = false;
  };

  const handleOnMenuLeave = () => {
    mouseInBounds.current = true;
  };

  const handleMenuInteractOutside = () => {
    menuClosedByOutsideInteractionRef.current = true;
  };

  const onCloseAutoFocus = (e: Event) => {
    if (menuClosedByOutsideInteractionRef.current) {
      menuClosedByOutsideInteractionRef.current = false;
      e.preventDefault();
      setIsToolbarFocused(false);
      return;
    }

    if (
      ((lastItemSelected === INTERACTION_MODE.Explore && onClose) ||
        lastItemSelected === INTERACTION_MODE.Zoom) &&
      onClose
    ) {
      // Prevent the autofocus from focusing back on the menu trigger after closing the menu
      e.preventDefault();
      onClose();
      updateLastItemSelected(undefined);
    }
  };

  const handleResetDidDrag = useCallback(() => {
    setDidDrag(false);
  }, [setDidDrag]);
  const showCollapsedIntentAndDownloadSubmenu =
    (!isExpanded || !fitsBounds) && (!!intents?.length || isDownloadEnabled);
  const showExpandButton = !isExpanded && fitsBounds;
  const showCollapseButton = isExpanded && isCollapsable;
  const showIntentMenu =
    isExpanded && fitsBounds && !!(intents?.length || intentsAsyncTemplate);

  const toolbarMenuConfig = useMemo<ChartToolbarContextProps>(
    () => ({
      isMenuElement: true,
      isExpanded,
      lastItemSelected,
      updateLastItemSelected,
    }),
    [isExpanded, lastItemSelected, updateLastItemSelected],
  );

  const intentsMenuConfig = useMemo<ChartToolbarContextProps>(
    () => ({
      isMenuElement: false,
      isExpanded,
      lastItemSelected,
      didDrag,
      resetDidDrag: handleResetDidDrag,
      updateLastItemSelected,
    }),
    [
      isExpanded,
      lastItemSelected,
      didDrag,
      handleResetDidDrag,
      updateLastItemSelected,
    ],
  );

  const handleKeyDown = (event: ReactKeyboardEvent) => {
    if (event.target === triggerDragRef.current && event.code !== 'Tab') {
      event.stopPropagation();
      event.preventDefault();
      return;
    }

    if (event.code === 'ArrowLeft' || event.code === 'ArrowRight') {
      event.stopPropagation();
      setKeyEvent(event);
    }

    if (event.code === 'Enter') {
      event.stopPropagation();
    }
  };

  const handleOnFocus = () => {
    // Ignore the synchronous autofocus that a child control's roving-tabindex
    // effect fires while the toolbar itself is still mounting, so a control
    // becoming the initial tabIndex=0 target doesn't force the toolbar
    // visible on its own.
    if (hasMountedRef.current) {
      setIsToolbarFocused(true);
    }
  };

  const handleOnBlur = (event: ReactFocusEvent) => {
    event.stopPropagation();
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setIsToolbarFocused(false);
    }
  };

  return (
    <ChartToolbarVisibilityOverlay
      handlerRef={handlerRef}
      boundSize={boundSize}
      setIsVisible={setIsVisible}
      visibleWhenMouseInBounds={visibleWhenMouseInBounds}
      isVisible={isVisible}
    >
      <div
        className={clsx(
          chartToolbarArea({
            inline: isInlineMode,
          }),
          overlayClassName,
        )}
        data-testid="chart-plot-overlay-toolbar"
        ref={boundRef}
        style={{ zIndex }}
      >
        <div
          className={chartToolbar({ inline: isInlineMode })}
          id="chart-toolbar"
          ref={toolbarRef}
          role="toolbar"
          data-testid="chart-toolbar-container"
          onFocus={handleOnFocus}
          onBlur={handleOnBlur}
          onKeyDown={handleKeyDown}
          onPointerDown={onMouseEnterHandler}
          onMouseEnter={onMouseEnterHandler}
          onMouseLeave={onMouseLeaveHandler}
          {...behaviorTrackingProps}
        >
          <A11yToolbarTabIndexContext.Provider value={a11yControlsValue}>
            <div
              className={outerContainer({
                visible:
                  visible ||
                  isMenuOpen ||
                  isDragging ||
                  isToolbarFocused ||
                  (visibleWhenMouseInBounds && isVisible),
                inline: isInlineMode,
              })}
              data-testid="chart-toolbar-wrapper"
              ref={childrenRef}
            >
              <Flex gap={0} flexDirection="row" alignItems="center">
                {draggable &&
                  !removeDragHandle && [
                    <Tooltip
                      text={intl.formatMessage(
                        TOOLBAR_MESSAGES.chartToolbarMove,
                      )}
                      disabled={isDragging}
                      key="tooltip"
                    >
                      <Button
                        className={grip}
                        ref={triggerDragRef as RefObject<HTMLButtonElement>}
                        variant="default"
                        size="condensed"
                        data-testid="chart-toolbar-grip"
                        aria-label={TOOLBAR_DRAG_BUTTON_ARIA_LABEL}
                      >
                        <Button.Prefix>
                          <DragAllDirectionIcon />
                        </Button.Prefix>
                      </Button>
                    </Tooltip>,
                    <VerticalDivider key="divider" />,
                  ]}
                <div
                  data-testid="toolbar-children"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    touchAction: 'none',
                  }}
                  ref={
                    draggable && removeDragHandle
                      ? (triggerDragRef as RefObject<HTMLDivElement>)
                      : undefined
                  }
                >
                  {shouldShowMenu && (
                    <ChartToolbarContext.Provider value={toolbarMenuConfig}>
                      <ToolbarMenu
                        onMenuOpen={handleToggleMenu}
                        activeModeIcon={menuIcon}
                        triggerLabel={menuTriggerLabel}
                        onMenuEnter={handleOnMenuEnter}
                        onMenuLeave={handleOnMenuLeave}
                        menuAlignment={isExpanded ? 'start' : 'end'}
                        onCloseAutoFocus={onCloseAutoFocus}
                        onInteractOutside={handleMenuInteractOutside}
                        data-dt-name="ToolbarTools"
                        data-dt-properties={
                          isExpanded && fitsBounds
                            ? 'state:changeMode'
                            : 'state:tools'
                        }
                      >
                        {children}
                        <IntentsCollapsedAndDownloadSection
                          hidden={!showCollapsedIntentAndDownloadSubmenu}
                          intents={intents}
                          showDownloadButton={isDownloadEnabled}
                          handleDownloadDataToolbar={handleDownloadDataToolbar}
                        />
                        {showExpandButton && (
                          <>
                            <HorizontalDivider />
                            {expandButton}
                          </>
                        )}
                      </ToolbarMenu>
                    </ChartToolbarContext.Provider>
                  )}
                  <ChartToolbarContext.Provider value={intentsMenuConfig}>
                    {children}
                    {showIntentMenu && (
                      <ChartDataMenu
                        onMenuOpen={handleToggleMenu}
                        onMenuEnter={handleOnMenuEnter}
                        onMenuLeave={handleOnMenuLeave}
                        isDownloadEnabled={isDownloadEnabled}
                        handleDownloadDataToolbar={handleDownloadDataToolbar}
                        intents={intents}
                        showDivider={showMenuDivider}
                        isDragging={isDragging}
                        buttonMode={isInlineMode ? 'inline' : 'standard'}
                        intentsAsyncTemplate={intentsAsyncTemplate}
                      />
                    )}
                  </ChartToolbarContext.Provider>
                  {showCollapseButton && collapseButton}
                </div>
              </Flex>
            </div>
          </A11yToolbarTabIndexContext.Provider>
        </div>
      </div>
    </ChartToolbarVisibilityOverlay>
  );
};

InternalChartToolbarRenderer['displayName'] = 'ChartToolbarRenderer';

/**
 * @internal
 */
export const ChartToolbarRenderer = Object.assign(
  InternalChartToolbarRenderer,
  {
    Control,
  },
);
